import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo";
import { adminMe, adminUpdateServicesPage, fetchServicesPage } from "../lib/api";
import { AdminHeader } from "../components/AdminNav";

const blankItem = { number: "", title: "", description: "" };

export default function AdminServicesPage() {
    const [page, setPage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        adminMe().catch(() => navigate("/admin/login", { replace: true }));
    }, [navigate]);

    useEffect(() => {
        fetchServicesPage().then(setPage).catch((e) => setError(e?.message || "Load failed"));
    }, []);

    const set = (key, val) => setPage((p) => ({ ...p, [key]: val }));
    const setItem = (i, key, val) => {
        setPage((p) => {
            const next = [...(p.items || [])];
            next[i] = { ...next[i], [key]: val };
            return { ...p, items: next };
        });
    };
    const addItem = () => setPage((p) => ({ ...p, items: [...(p.items || []), { ...blankItem }] }));
    const removeItem = (i) =>
        setPage((p) => ({ ...p, items: (p.items || []).filter((_, idx) => idx !== i) }));
    const move = (i, dir) => {
        setPage((p) => {
            const next = [...(p.items || [])];
            const j = i + dir;
            if (j < 0 || j >= next.length) return p;
            [next[i], next[j]] = [next[j], next[i]];
            return { ...p, items: next };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            await adminUpdateServicesPage(page);
            setSavedAt(new Date());
        } catch (err) {
            setError(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!page) {
        return (
            <main className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16">
                <p className="asc-overline">Loading…</p>
                {error && <p className="text-red-700 text-sm mt-4">{error}</p>}
            </main>
        );
    }

    const linkCls = "text-[11px] uppercase tracking-[0.22em] text-[#5C5855] hover:text-[#1A1A1A]";

    return (
        <main data-testid="page-admin-services-page" className="min-h-screen bg-[#F9F8F6] px-6 md:px-12 lg:px-24 py-16 md:py-24">
            <Seo path="/admin/services-page" title="Services Page — Admin" description="Manage the Services page" noindex />
            <AdminHeader title="Services page" />

            {error && <p className="mt-6 text-red-700 text-sm" data-testid="services-page-error">{error}</p>}
            {savedAt && <p className="mt-6 text-green-700 text-sm" data-testid="services-page-saved">Saved at {savedAt.toLocaleTimeString()}.</p>}

            <form onSubmit={submit} className="mt-10 max-w-3xl space-y-10">
                <Section title="Header">
                    <Field label="Eyebrow (small overline)">
                        <input data-testid="sp-eyebrow" value={page.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Headline — line 1">
                        <input data-testid="sp-title-1" value={page.title_line_1 || ""} onChange={(e) => set("title_line_1", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Headline — italic word" hint="Rendered italic between line 1 and line 2">
                        <input data-testid="sp-title-italic" value={page.title_italic || ""} onChange={(e) => set("title_italic", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Headline — line 2">
                        <input data-testid="sp-title-2" value={page.title_line_2 || ""} onChange={(e) => set("title_line_2", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Intro paragraph">
                        <textarea data-testid="sp-intro" rows={3} value={page.intro || ""} onChange={(e) => set("intro", e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <Section title="Services">
                    <p className="text-sm text-[#5C5855] max-w-xl">
                        Each item is a discipline shown on the Services page. Reorder with ↑/↓, remove, or add new.
                    </p>
                    {(page.items || []).map((it, i) => (
                        <div
                            key={i}
                            data-testid={`sp-item-${i}`}
                            className="border border-[#E5E1DB] bg-white p-5 space-y-4"
                        >
                            <div className="flex items-center justify-between">
                                <p className="asc-overline text-[#8C7A6B]">Item {i + 1}</p>
                                <div className="flex items-center gap-2">
                                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`sp-up-${i}`} className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1">↑</button>
                                    <button type="button" onClick={() => move(i, 1)} disabled={i === page.items.length - 1} data-testid={`sp-down-${i}`} className="text-xs text-[#5C5855] disabled:opacity-30 px-2 py-1">↓</button>
                                    <button type="button" onClick={() => removeItem(i)} data-testid={`sp-remove-${i}`} className="text-[10px] uppercase tracking-[0.18em] text-red-700 px-2 py-1">Remove</button>
                                </div>
                            </div>
                            <Field label="Number (e.g. 01)">
                                <input data-testid={`sp-number-${i}`} value={it.number} onChange={(e) => setItem(i, "number", e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Title">
                                <input data-testid={`sp-title-${i}`} value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} className={inputCls} />
                            </Field>
                            <Field label="Description">
                                <textarea data-testid={`sp-desc-${i}`} rows={3} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} className={inputCls} />
                            </Field>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addItem}
                        data-testid="sp-add-item"
                        className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#F9F8F6]"
                    >
                        + Add service
                    </button>
                </Section>

                <Section title="Call-to-action">
                    <Field label="CTA eyebrow">
                        <input data-testid="sp-cta-eyebrow" value={page.cta_eyebrow || ""} onChange={(e) => set("cta_eyebrow", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="CTA heading — line 1">
                        <input data-testid="sp-cta-title-1" value={page.cta_title_line_1 || ""} onChange={(e) => set("cta_title_line_1", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="CTA heading — italic word">
                        <input data-testid="sp-cta-italic" value={page.cta_title_italic || ""} onChange={(e) => set("cta_title_italic", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="CTA heading — line 2">
                        <input data-testid="sp-cta-title-2" value={page.cta_title_line_2 || ""} onChange={(e) => set("cta_title_line_2", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Button label">
                        <input data-testid="sp-cta-label" value={page.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={inputCls} />
                    </Field>
                    <Field label="Button link" hint="Internal path (e.g. /contact) or full URL">
                        <input data-testid="sp-cta-link" value={page.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <div className="flex items-center gap-6 pt-6 border-t border-[#E5E1DB]">
                    <button
                        type="submit"
                        data-testid="services-page-save"
                        disabled={saving}
                        className="px-8 py-3 bg-[#1A1A1A] text-[#F9F8F6] text-[11px] uppercase tracking-[0.22em] hover:bg-black disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save services page"}
                    </button>
                </div>
            </form>
        </main>
    );
}

const inputCls = "w-full bg-white border border-[#E5E1DB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm text-[#1A1A1A]";

const Section = ({ title, children }) => (
    <section>
        <p className="asc-overline mb-4">{title}</p>
        <div className="space-y-5">{children}</div>
    </section>
);

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.22em] text-[#8C7A6B] mb-2">
            {label}
            {hint && <span className="ml-2 lowercase tracking-normal text-[#A89F94] normal-case">— {hint}</span>}
        </span>
        {children}
    </label>
);
